import { useEffect, useRef, useState } from 'react';
import axios, { type CancelTokenSource } from 'axios';

import { useEnrollments } from '../contexts/enrollments/context';
import { QUERY_LIMIT, START_PAGE_SIZE } from '../common/constants';
import { EnrollmentInterface, QueryGetEnrollmentsInterface } from '../interfaces/enrollment';

interface UseInfiniteEnrollmentsProps {
  initialQuery?: Partial<QueryGetEnrollmentsInterface>;
}

const useInfiniteEnrollments = ({ initialQuery = {} }: UseInfiniteEnrollmentsProps = {}) => {
  const { fetchEnrollments } = useEnrollments();

  const [queryParam, setQueryParam] = useState<QueryGetEnrollmentsInterface>({
    limit: QUERY_LIMIT,
    moreInfo: true,
    page: START_PAGE_SIZE,
    state: 'enrolled,inactive',
    ...initialQuery,
  });

  const [items, setItems] = useState<EnrollmentInterface[]>([]);
  const [totalItems, setTotalItems] = useState<number>(0);
  const [hasMore, setHasMore] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const cancelTokenRef = useRef<CancelTokenSource | null>(null);
  const isRequestActive = useRef<boolean>(false);

  const reset = (attributesToUpdate: Partial<QueryGetEnrollmentsInterface> = {}) => {
    isRequestActive.current = false;
    if (cancelTokenRef.current) cancelTokenRef.current.cancel('Reset');
    setItems([]);
    setQueryParam((prev) => ({
      ...prev,
      ...attributesToUpdate,
      page: START_PAGE_SIZE,
    }));
  };

  const fetchNextPage = () => {
    if (!isLoading && hasMore) {
      setQueryParam((prev) => ({ ...prev, page: prev.page + 1 }));
    }
  };

  const performFetch = async (query: QueryGetEnrollmentsInterface) => {
    if (cancelTokenRef.current) cancelTokenRef.current.cancel('Operation canceled due to a new request.');
    cancelTokenRef.current = axios.CancelToken.source();
    return fetchEnrollments(query, cancelTokenRef.current.token);
  };

  const fetchData = async () => {
    setIsLoading(true);
    isRequestActive.current = true;

    try {
      const result = await performFetch(queryParam);
      if (result && isRequestActive.current) {
        const { enrollments, total } = result;

        const existingIds = new Set(items.map((i) => i._id));
        const uniqueItems = enrollments.filter((e) => !existingIds.has(e._id));

        setItems((prev) => (prev.length ? [...prev, ...uniqueItems] : enrollments));
        setTotalItems(total);
        setHasMore(enrollments.length === QUERY_LIMIT);
      }
    } finally {
      if (isRequestActive.current) {
        setIsLoading(false);
        isRequestActive.current = false;
      }
    }
  };

  useEffect(() => {
    fetchData();
    return () => {
      cancelTokenRef.current?.cancel('Unmounted');
      isRequestActive.current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryParam]);

  return {
    hasMore,
    isLoading,
    items,
    queryParam,
    totalItems,
    fetchNextPage,
    reset,
  };
};

export default useInfiniteEnrollments;
